import React, { useState, useEffect } from 'react';
import { Car, RefreshCw, Save, Info, AlertCircle } from 'lucide-react';
import { mileageAPI } from '../api/client';

// HMRC 2026/27 reference data (hardcoded for display — kept in sync with DB seeds)
const HMRC_REFERENCE = [
  { label: 'Cars and vans', first10k: '55p', over10k: '25p', allMiles: null },
  { label: 'Motorcycles',   first10k: null,  over10k: null,  allMiles: '24p' },
  { label: 'Bicycles',      first10k: null,  over10k: null,  allMiles: '20p' },
  { label: 'Passenger supplement', first10k: null, over10k: null, allMiles: '5p per passenger' },
];

const FIELD_CONFIG = [
  {
    key:    'Car_standard',
    dbType: 'Car',
    label:  'Car / Van — first 10,000 miles',
    hmrc:   '0.5500',
      note:   "Applies until employee's year-to-date mileage reaches 10,000",
    thresholdMiles: 10000,
  },
  {
    key:    'Car_threshold',
    dbType: 'Car_Over10k',
    label:  'Car / Van — over 10,000 miles',
    hmrc:   '0.2500',
    note:   'Applies once year-to-date mileage exceeds 10,000',
    thresholdMiles: null,
  },
  {
    key:    'Motorcycle',
    dbType: 'Motorcycle',
    label:  'Motorcycle',
    hmrc:   '0.2400',
    note:   'All miles',
    thresholdMiles: null,
  },
  {
    key:    'Bicycle',
    dbType: 'Bicycle',
    label:  'Bicycle',
    hmrc:   '0.2000',
    note:   'All miles',
    thresholdMiles: null,
  },
  {
    key:    'Passenger',
    dbType: 'Passenger Supplement',
    label:  'Passenger supplement',
    hmrc:   '0.0500',
    note:   'Per passenger per business mile',
    thresholdMiles: null,
  },
];

const emptyForm = () =>
  Object.fromEntries(FIELD_CONFIG.map(f => [f.key, '']));

export default function MileageRateSettings() {
  const [companyRates, setCompanyRates] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', text }

  useEffect(() => {
    loadCompanyRates();
  }, []);

  const loadCompanyRates = async () => {
    try {
      setLoading(true);
      const res = await mileageAPI.getCompanyRates();
      const rates = res.data?.rates ?? [];
      setCompanyRates(rates);

      // Pre-fill form from existing overrides
      const next = emptyForm();
      rates.forEach(r => {
        const field = FIELD_CONFIG.find(f => f.dbType === r.vehicleType);
        if (field) next[field.key] = r.standardRate ?? '';
      });
      setForm(next);
    } catch (err) {
      console.error('Failed to load company mileage rates:', err);
      setFeedback({ type: 'error', text: 'Could not load company rate settings.' });
    } finally {
      setLoading(false);
    }
  };

  const hasOverrides = companyRates.length > 0;

  const handleSave = async () => {
    const rates = FIELD_CONFIG
      .filter(f => form[f.key] !== '' && form[f.key] != null)
      .map(f => ({
        vehicleType:   f.dbType,
        ratePerMile:   parseFloat(form[f.key]),
        thresholdMiles: f.thresholdMiles,
      }));

    if (rates.length === 0) {
      setFeedback({ type: 'error', text: 'Enter at least one rate before saving.' });
      return;
    }

    // Basic validation
    for (const r of rates) {
      if (isNaN(r.ratePerMile) || r.ratePerMile <= 0) {
        setFeedback({ type: 'error', text: `Invalid value for ${r.vehicleType}. Please enter a positive number.` });
        return;
      }
    }

    try {
      setSaving(true);
      setFeedback(null);
      await mileageAPI.saveCompanyRates({ rates });
      setFeedback({ type: 'success', text: `Rate card saved — ${rates.length} rate(s) applied to all expense calculations.` });
      await loadCompanyRates();
    } catch (err) {
      console.error('Failed to save company mileage rates:', err);
      setFeedback({ type: 'error', text: 'Failed to save. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Remove all company-specific rates and revert to HMRC defaults?')) return;
    try {
      setResetting(true);
      setFeedback(null);
      await mileageAPI.deleteCompanyRates();
      setForm(emptyForm());
      setFeedback({ type: 'success', text: 'Company overrides removed — HMRC defaults now apply.' });
      await loadCompanyRates();
    } catch (err) {
      console.error('Failed to reset mileage rates:', err);
      setFeedback({ type: 'error', text: 'Failed to reset rates. Please try again.' });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6 px-4">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Car className="h-6 w-6 text-primary-600" />
          Mileage Rate Settings
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Set the rate per mile for each vehicle type. Your company rates override HMRC defaults for
          all employees. The 10,000-mile tax-year threshold is always applied automatically.
        </p>
      </div>

      {/* HMRC reference panel */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <h2 className="font-semibold text-blue-900 text-sm">
            HMRC Approved Mileage Rates — 2026/27 tax year (effective 6 April 2026)
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-blue-800 border-b border-blue-200">
                <th className="pb-2 font-medium">Vehicle</th>
                <th className="pb-2 font-medium">First 10,000 miles</th>
                <th className="pb-2 font-medium">Over 10,000 miles / all miles</th>
              </tr>
            </thead>
            <tbody>
              {HMRC_REFERENCE.map(row => (
                <tr key={row.label} className="border-t border-blue-100 text-blue-900">
                  <td className="py-2">{row.label}</td>
                  <td className="py-2 font-semibold">{row.first10k ?? row.allMiles ?? '—'}</td>
                  <td className="py-2">{row.over10k ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-blue-600 mt-3">
          Source: GOV.UK. Rates updated 21 May 2026 — car/van rate increased from 45p to 55p (first change since 2011).
          Future HMRC rate changes are applied automatically at the start of each tax year.
        </p>
      </div>

      {/* Company rate card form */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="font-semibold text-gray-900">Your Company Rate Card</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {hasOverrides
                ? 'Your company is using custom rates — these override HMRC defaults for all employees.'
                : 'Using HMRC defaults. Enter values below to apply company-specific rates.'}
            </p>
          </div>
          {hasOverrides && (
            <button
              onClick={handleReset}
              disabled={resetting}
              className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 shrink-0 ml-4"
            >
              <RefreshCw className={`h-4 w-4 ${resetting ? 'animate-spin' : ''}`} />
              {resetting ? 'Resetting…' : 'Reset to HMRC Defaults'}
            </button>
          )}
        </div>

        <div className="space-y-3">
          {FIELD_CONFIG.map(f => (
            <div key={f.key} className="flex items-center gap-4 border border-gray-100 rounded-lg px-4 py-3 bg-gray-50">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800">{f.label}</div>
                <div className="text-xs text-gray-400 mt-0.5">{f.note}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-gray-400 whitespace-nowrap">HMRC: £{f.hmrc}</span>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder={f.hmrc}
                    value={form[f.key]}
                    onChange={e => setForm(v => ({ ...v, [f.key]: e.target.value }))}
                    className="pl-6 pr-3 py-2 w-28 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Feedback */}
        {feedback && (
          <div
            className={`mt-4 flex items-start gap-2 rounded-lg px-4 py-3 text-sm border ${
              feedback.type === 'success'
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-red-50 text-red-800 border-red-200'
            }`}
          >
            {feedback.type === 'error' && <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />}
            {feedback.text}
          </div>
        )}

        {/* Actions */}
        <div className="mt-5 flex items-center justify-between pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Leave a field blank to keep the HMRC default for that vehicle type.
            All changes apply to new expense submissions immediately.
          </p>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save Rate Card'}
          </button>
        </div>
      </div>
    </div>
  );
}
